import axios from "axios";

import type {
  CnpjLookup,
  Organization,
  OrganizationAddress,
  OrganizationCreatePayload,
  OrganizationUpdatePayload,
} from "@/types/organization";

import { api, unwrap } from "./axiosConfig";

// Mirrors backend app/schemas/organizations/contact.py::OrganizationContactResponse.
export interface OrganizationContact {
  id: number;
  kind: string;
  name: string;
}

// Mirrors backend app/schemas/organizations/certificate.py::CertificateResponse.
export interface OrganizationCertificate {
  id: number;
  filename: string;
  valid_until: string | null;
  active: boolean;
  created_at: string;
}

const base = (wsId: number) => `/api/v1/workspaces/${wsId}/organizations`;

export const organizationService = {
  async list(workspaceId: number): Promise<Organization[]> {
    return unwrap(api.get(base(workspaceId)));
  },

  async get(workspaceId: number, orgId: number): Promise<Organization> {
    return unwrap(api.get(`${base(workspaceId)}/${orgId}`));
  },

  async create(workspaceId: number, payload: OrganizationCreatePayload): Promise<Organization> {
    return unwrap(api.post(base(workspaceId), payload));
  },

  async update(
    workspaceId: number,
    orgId: number,
    payload: OrganizationUpdatePayload,
  ): Promise<Organization> {
    return unwrap(api.patch(`${base(workspaceId)}/${orgId}`, payload));
  },

  async remove(workspaceId: number, orgId: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${orgId}`);
  },

  /** Upsert do endereço 1:1 da organização. */
  async setAddress(
    workspaceId: number,
    orgId: number,
    payload: OrganizationAddress,
  ): Promise<OrganizationAddress> {
    return unwrap(api.put(`${base(workspaceId)}/${orgId}/address`, payload));
  },

  /** Substitui os contatos (comercial/financeiro) da organização. */
  async setContacts(
    workspaceId: number,
    orgId: number,
    contacts: Array<{ kind: string; name: string; email?: string | null; phone?: string | null }>,
  ): Promise<OrganizationContact[]> {
    return unwrap(api.put(`${base(workspaceId)}/${orgId}/contacts`, { contacts }));
  },

  /** Certificado A1 ativo da organização (null se não houver). */
  async getCertificate(
    workspaceId: number,
    orgId: number,
  ): Promise<OrganizationCertificate | null> {
    try {
      return await unwrap(api.get(`${base(workspaceId)}/${orgId}/certificate`));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  /** Anexa o certificado A1 (.pfx) da organização (multipart). */
  async uploadCertificate(
    workspaceId: number,
    orgId: number,
    file: File,
    password: string,
  ): Promise<OrganizationCertificate> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("password", password);
    fd.append("accepted", "true");
    return unwrap(api.post(`${base(workspaceId)}/${orgId}/certificate`, fd));
  },

  async revokeCertificate(workspaceId: number, orgId: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${orgId}/certificate`);
  },
};

export const lookupService = {
  async cnpj(cnpj: string): Promise<CnpjLookup> {
    const digits = cnpj.replace(/\D/g, "");
    return unwrap(api.get(`/api/v1/lookup/cnpj/${digits}`));
  },
};
